import React, { useState, useEffect } from 'react';
import ComicButton from '../components/ui/ComicButton';
import SpeechBubble from '../components/ui/SpeechBubble';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMic, FiAward, FiStar, FiCalendar, FiMapPin, FiZap, FiBookOpen } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';

const PosterGala = () => (
  <div className="w-full h-full bg-hcf-gradient p-6 flex flex-col justify-between text-white border-2 border-white/20 relative overflow-hidden">
    <div className="absolute top-0 right-0 p-4 opacity-20 transform rotate-12">
      <SafeIcon icon={FiStar} className="text-9xl" />
    </div>
    <div className="relative z-10">
      <div className="bg-hcf-yellow text-black inline-block px-3 py-1 font-comic text-xl mb-4 transform -rotate-2 shadow-comic-sm">
        OPENING NIGHT
      </div>
      <h2 className="font-comic text-6xl leading-none drop-shadow-md mb-2 text-white"> GRAND<br/>GALA<br/>SHOW </h2>
      <p className="font-body text-xl font-bold text-hcf-yellow italic">THE WHITE ROCK THEATRE</p>
    </div>
    <div className="relative z-10 mt-4">
      <div className="flex items-center gap-2 text-lg font-bold mb-4 bg-black/30 w-fit px-3 py-1 rounded">
        <SafeIcon icon={FiCalendar} />
        <span>JUNE 14th 2026</span>
      </div>
      <button className="w-full bg-white text-hcf-pink font-comic text-2xl py-3 hover:bg-black hover:text-white transition-all border-4 border-black shadow-comic-sm">
        BOOK TICKETS
      </button>
    </div>
  </div>
);

const PosterNewcomer = () => (
  <div className="w-full h-full bg-hcf-gradient-reverse p-6 flex flex-col justify-between text-white relative overflow-hidden">
    <div className="absolute -bottom-10 -right-10 p-4 opacity-10">
      <SafeIcon icon={FiAward} className="text-[12rem]" />
    </div>
    <div className="relative z-10 text-center border-4 border-white p-4 h-full flex flex-col justify-center items-center">
      <div className="animate-bounce">
        <SafeIcon icon={FiMic} className="text-6xl mb-4 text-hcf-yellow drop-shadow-lg" />
      </div>
      <h2 className="font-comic text-5xl leading-none mb-4 text-white"> NEWCOMER<br/>OF THE<br/>YEAR </h2>
      <div className="bg-hcf-pink px-4 py-1 transform rotate-2 mb-6 border-2 border-white">
        <span className="font-comic text-2xl uppercase text-white">Applications Open</span>
      </div>
      <p className="font-body text-sm font-bold mb-6 tracking-widest uppercase text-white">The future stars of stand-up</p>
      <button className="bg-hcf-yellow text-black font-comic text-2xl px-8 py-3 shadow-[4px_4px_0_rgba(0,0,0,1)] hover:translate-y-1 hover:shadow-none transition-all border-2 border-black">
        APPLY NOW
      </button>
    </div>
  </div>
);

const PosterFringe = () => (
  <div className="w-full h-full bg-black p-4 flex flex-col relative overflow-hidden">
    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/comic-dots.png')] opacity-30"></div>
    <div className="h-full border-4 border-dashed border-hcf-cyan p-6 flex flex-col justify-center items-center relative z-10">
      <div className="bg-hcf-cyan text-black font-comic px-4 py-1 mb-4 rotate-1"> HASTINGS & ST LEONARDS </div>
      <h2 className="font-comic text-5xl text-white text-center mb-4 leading-none strike-text">
        <span className="text-hcf-pink">LOCAL</span><br/>FRINGE
      </h2>
      <ul className="text-white font-body text-lg space-y-3 text-center mb-8 w-full">
        <li className="bg-white/10 py-1 rounded-full border border-white/20">The Jenny Lind</li>
        <li className="bg-white/10 py-1 rounded-full border border-white/20">The Albion</li>
        <li className="bg-white/10 py-1 rounded-full border border-white/20">The Crypt</li>
      </ul>
      <div className="bg-hcf-yellow text-black font-comic text-3xl px-6 py-2 transform -rotate-3 text-center border-4 border-black shadow-comic-sm">
        50+ SHOWS
      </div>
    </div>
  </div>
);

const posters = [PosterGala, PosterNewcomer, PosterFringe];

const Home = () => {
  const [currentPosterIndex, setCurrentPosterIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentPosterIndex((prev) => (prev + 1) % posters.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const CurrentPoster = posters[currentPosterIndex];

  const sponsors = [
    "Lightning Fibre",
    "Love Hastings.com",
    "World Fringe",
    "British Comedy Guide",
    "Next Up...",
    "The Hastings Flyer",
    "Live Comedy Association",
    "DR Studio",
    "JokePit",
    "FullyD"
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Hero Section */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-7 flex flex-col justify-center relative z-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0, x: -50 }}
            animate={{ scale: 1, opacity: 1, x: 0 }}
            transition={{ type: 'spring', bounce: 0.5 }}
          >
            <SpeechBubble direction="left" className="w-full md:w-3/4 ml-4 md:ml-12 border-hcf-pink shadow-glow">
              <span className="text-hcf-pink font-bold uppercase text-xl">Laughter is coming!</span>
              <br /> The 2026 Festival dates are officially June 14-20!
            </SpeechBubble>
          </motion.div>
          <div className="bg-hcf-gradient border-4 border-black p-8 shadow-comic transform -rotate-1 relative overflow-hidden text-white">
            <div className="absolute top-0 left-0 w-full h-full bg-black/10 z-0"></div>
            <h1 className="font-comic text-6xl md:text-8xl leading-none mb-4 relative z-10 drop-shadow-[4px_4px_0_rgba(0,0,0,1)]">
              HASTINGS <br />
              <span className="text-hcf-yellow">COMEDY</span> <br />
              FESTIVAL
            </h1>
            <div className="flex items-center gap-4 mb-8 relative z-10">
              <div className="bg-hcf-yellow text-black font-comic text-2xl px-4 py-1 transform -rotate-2 border-2 border-black">
                JUNE 2026
              </div>
              <div className="font-body text-xl md:text-2xl font-bold uppercase text-white drop-shadow-md">
                7 Days of Chaos
              </div>
            </div>
            <div className="flex flex-wrap gap-4 relative z-10">
              <ComicButton variant="primary" className="text-2xl bg-hcf-yellow text-black hover:bg-white border-white">
                Buy Tickets
              </ComicButton>
              <ComicButton variant="primary" className="text-2xl bg-hcf-cyan text-white hover:bg-black hover:text-white border-white">
                View Lineup
              </ComicButton>
            </div>
          </div>
        </div>

        {/* Carousel Section */}
        <div className="md:col-span-5 relative h-[450px] md:h-[550px]">
          <div className="absolute inset-0 transform rotate-2 bg-hcf-pink rounded-lg translate-x-3 translate-y-3 border-4 border-black"></div>
          <div className="relative h-full w-full bg-white border-4 border-black p-2 shadow-comic overflow-hidden">
            <div className="w-full h-full border-2 border-black relative overflow-hidden bg-gray-100">
              <AnimatePresence mode='wait'>
                <motion.div
                  key={currentPosterIndex}
                  className="absolute inset-0"
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.6 }}
                >
                  <CurrentPoster />
                </motion.div>
              </AnimatePresence>

              {/* Carousel Navigation Markers */}
              <div className="absolute bottom-4 left-0 w-full flex justify-center gap-3 z-30">
                {posters.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPosterIndex(idx)}
                    className={`
                      w-4 h-4 border-2 border-black transform rotate-45 transition-all
                      ${currentPosterIndex === idx 
                        ? 'bg-hcf-yellow scale-125 shadow-[2px_2px_0_#000]' 
                        : 'bg-white hover:bg-hcf-cyan'}
                    `}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors Marquee Section */}
      <section className="mb-20">
        <div className="flex items-center gap-4 mb-6">
          <span className="font-comic text-xl text-black uppercase whitespace-nowrap">Our Festival Partners</span>
          <div className="h-1 bg-black flex-grow"></div>
        </div>
        <div className="border-4 border-black bg-hcf-yellow overflow-hidden py-4 relative shadow-comic-sm">
          <div className="flex whitespace-nowrap overflow-hidden">
            <div className="animate-marquee flex items-center gap-16 px-4">
              {[...Array(2)].map((_, i) => (
                <React.Fragment key={i}>
                  {sponsors.map((sponsor, sIdx) => (
                    <React.Fragment key={`${i}-${sIdx}`}>
                      <span className="font-comic text-3xl text-black uppercase hover:text-hcf-pink cursor-default transition-colors">
                        {sponsor}
                      </span>
                      <SafeIcon icon={FiZap} className="text-hcf-pink text-4xl" />
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Program Teaser Section */}
      <section className="mb-20">
        <div className="bg-hcf-purple border-8 border-black p-10 flex flex-col md:flex-row items-center gap-10 shadow-comic relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/comic-dots.png')] opacity-20"></div>
          <div className="md:w-1/4 rotate-[-3deg] relative z-10">
            <div className="bg-white border-4 border-black p-2 shadow-comic group cursor-pointer transition-all hover:rotate-0">
              <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1770308908583-image.png" className="w-full border-2 border-black" alt="Program Cover" />
              <div className="absolute -bottom-4 -right-4 bg-hcf-yellow text-black font-comic px-4 py-1 border-2 border-black rotate-12"> NEW! </div>
            </div>
          </div>
          <div className="md:w-3/4 relative z-10 text-center md:text-left">
            <h2 className="font-comic text-5xl md:text-7xl text-hcf-yellow mb-4 drop-shadow-[4px_4px_0_#000]">DIGITAL PROGRAM</h2>
            <p className="font-body text-2xl text-white mb-8 font-bold leading-tight">THE FULL 2026 FESTIVAL GUIDE IS HERE. DON'T MISS A SINGLE LAUGH!</p>
            <Link to="/program">
              <ComicButton variant="primary" className="text-2xl bg-hcf-cyan border-white text-white hover:bg-white hover:text-hcf-cyan">
                VIEW PROGRAM ONLINE
              </ComicButton>
            </Link>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="mb-20">
        <div className="flex items-center gap-4 mb-12">
          <div className="bg-hcf-pink text-white border-4 border-black shadow-comic-sm px-6 py-2 font-comic text-4xl transform -skew-x-12"> LATEST NEWS </div>
          <div className="h-2 bg-hcf-gradient flex-grow border-y-2 border-black"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 border-4 border-black shadow-comic hover:-rotate-1 transition-all group">
            <div className="h-48 bg-hcf-cyan border-2 border-black mb-4 overflow-hidden relative">
              <img src="https://images.unsplash.com/photo-1543584730-05c42d38448a?q=80&w=600&auto=format&fit=crop" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="Venue" />
            </div>
            <h3 className="font-comic text-2xl mb-2 text-hcf-purple">NEW ACTS ANNOUNCED</h3>
            <p className="font-body leading-tight">The first wave of headliners for 2026 has landed! Check out who's coming to Hastings.</p>
          </div>
          <div className="bg-white p-6 border-4 border-black shadow-comic hover:rotate-1 transition-all group">
            <div className="h-48 bg-hcf-pink border-2 border-black mb-4 overflow-hidden relative">
              <img src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?q=80&w=600&auto=format&fit=crop" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="Microphone" />
            </div>
            <h3 className="font-comic text-2xl mb-2 text-hcf-pink uppercase">Newcomer Awards</h3>
            <p className="font-body leading-tight">Applications are open! We're looking for the next big star in stand-up comedy. Apply now.</p>
          </div>
          <div className="bg-white p-6 border-4 border-black shadow-comic hover:-rotate-1 transition-all group">
            <div className="h-48 bg-hcf-yellow border-2 border-black mb-4 overflow-hidden relative">
              <img src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=600&auto=format&fit=crop" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="Festival" />
            </div>
            <h3 className="font-comic text-2xl mb-2 text-black">TICKETS ON SALE</h3>
            <p className="font-body leading-tight">Early bird passes for June 2026 are flying out! Grab yours before the price jump.</p>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-hcf-gradient border-8 border-black p-10 text-center shadow-comic relative overflow-hidden text-white">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/halftone.png')] opacity-10"></div>
        <h2 className="font-comic text-5xl md:text-7xl mb-6 text-white drop-shadow-[4px_4px_0_rgba(0,0,0,1)]">READY TO LAUGH?</h2>
        <p className="font-body text-2xl mb-8 max-w-2xl mx-auto font-bold text-white drop-shadow-md uppercase">Join us for the ultimate comedy experience on the coast.</p>
        <ComicButton variant="primary" className="text-3xl px-12 py-6 bg-hcf-yellow text-black hover:bg-white border-white">
          GET TICKETS
        </ComicButton>
      </section>
    </div>
  );
};

export default Home;